/**
 * groupSettings.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import { Alert, Button, Checkbox, FormControl, FormControlLabel, 
  FormLabel, IconButton, Radio, RadioGroup, Switch, TextField } from "@mui/material";


import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { Group, GroupType, GroupSubmissionType, GroupMembership,
  MemberNotifyOptions, ProtocolScheme } from "../models/group";

import { groupServiceFactory } from "../services/group.service";
import { dbServiceFactory } from "../services/db.service";
import { PermissionLevel } from "../models/auth";

interface GroupSettingsParams {
  groupID: string,
  [key: string]: string | undefined
}

export function GroupSettingsComponent(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const { groupID } = useParams<GroupSettingsParams>() as {groupID: string};
  const [errMsg, setErrMsg] = React.useState("");
  const [saving, setSaving] = React.useState<boolean>(false);
  const [group, setGroup] = React.useState<Group | null>(null);
  const [notifyOn, setNotifyOn] = React.useState<MemberNotifyOptions>(MemberNotifyOptions.mentions);
  const [nameTimer, setNameTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [descTimer, setDescTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [canToggleArchiving, setCanToggle] = React.useState<boolean>(false);
  const [fullyWelcomed, setFullyWelcomed] = React.useState(false);
  const [deviceAdded, setDeviceAdded] = React.useState(false);
  const [enableReset, setEnableReset] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(function () {
    const membership = authContext.membership;
    if (!membership) { return; }
    if (!groupNavContext.groupMemberships) { return; }
    if (groupNavContext.groupMemberships.length <= 0) { return; }

    const groupMembership: GroupMembership | undefined = groupNavContext.groupMemberships?.find(m =>
        m.groupID === groupID) || groupNavContext.directMessages?.find(m => m.groupID === groupID);

    if (!groupMembership) {
      console.error("No membership with groupID:", groupID);
      //setErrMsg("Group does not exist or you are not a member");
      //setLoading(false);
      navigate(`/group/${groupID}`);
      return;
    }
    const canToggleArchive = (membership.canArchive && !membership.mustArchive) ? true : false;
    if (groupMembership.grouptype === GroupType.dm && !canToggleArchive) {
      navigate(`/group/${groupID}`);
      return;
    }

    if (groupMembership.groupID !== groupNavContext.currentGroup?.groupID) {
      groupNavContext.setCurrentGroup(groupMembership);
    }

    if (groupMembership && groupMembership.notifyOn) {
      setNotifyOn(groupMembership.notifyOn);
    }

    setCanToggle(canToggleArchive);

    const groupService = groupServiceFactory();
    bluebird.all([
      groupService.getGroup(membership.accountID, groupID),
      groupService.getLocalGroup(groupID)
    ]).then(([g, localGroup]) => {
      console.log("group:", g);
      if (g && !g.submissiontype) {
          g.submissiontype = GroupSubmissionType.normal;
      }
      
      setFullyWelcomed(localGroup && groupService.isFullyWelcomed(localGroup) || false);
      setDeviceAdded(!!groupMembership.devices.find(dev => dev._id === groupService.deviceID));
      setGroup(g);
    }, err => {
      console.error("Error getting group:", err);
    });
  }, [authContext.membership, groupID, groupNavContext.groupMemberships.length]);

  async function performUpdate(update: Partial<Group>) {
    try {
      const membership = authContext.membership;
      if (!membership) { return; }
      console.log("update:", update);
      await groupServiceFactory().updateGroupSettings(membership.accountID, groupID, update);
      await dbServiceFactory().updateGroup(groupID, update);
    } catch (err) {
      console.error("Error saving update:", err);
      setErrMsg("Error updating group: " + (err as Error).message);
    }
  }
  function updateGroupName(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const newValue = inputElem.value;
    if (group) {
      group.groupname = newValue;
    }

    if (nameTimer) { clearTimeout(nameTimer); }
    setNameTimer(setTimeout(async () => {
      try {
        const membership = authContext.membership;
        if (!membership) { return; }
        const update = {groupname: newValue};
        await groupServiceFactory().updateGroupName(membership.accountID, groupID, update);
        await dbServiceFactory().updateGroup(groupID, update);
      } catch (err) {
        console.error("Error saving update:", err);
        setErrMsg("Error updating group name: " + (err as Error).message);
      }
    }, 2000));
  }

  function updateGroupDesc(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const newValue = inputElem.value;
    console.log("new desc:", newValue);
    if (group) {
      group.desc = newValue;
    }

    if (descTimer) { clearTimeout(descTimer); }
    setDescTimer(setTimeout(() => {
      performUpdate({desc: newValue});
    }, 2000));
  }

  function updateValue(update: Partial<Group>) {
    if (!group) { return; }
    setGroup({...group, ...update});
    performUpdate(update);
  }

  async function updateNotifyPreference(update: Partial<GroupMembership>) {
    try {
      const membership = authContext.membership;
      if (!membership) { return; }
      if (update.notifyOn) {
        setNotifyOn(update.notifyOn);
        if (groupNavContext.currentGroup) {
          groupNavContext.currentGroup.notifyOn = update.notifyOn;
        }
      }
      console.log("update:", update);
      await groupServiceFactory().updateGroupMemberSettings(membership.accountID, groupID, update);
    } catch (err) {
      console.error("Error saving update:", err);
      setErrMsg("Error saving notification preferences: " + (err as Error).message);
    }
  }

  async function toggleArchiving(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      const archiving = event.target.checked;
      console.log("new archiving: ", archiving);
      const membership = authContext.membership;
      if (!membership) { return; }
      if (!group) { return; }
      if (saving) { return; }

      setSaving(true);
      const groupService = groupServiceFactory();
      archiving ?
        await groupService.enableArchiving(membership.accountID, groupID) :
        await groupService.disableArchiving(membership.accountID, groupID)

      setGroup({...group, archiving: archiving});
    } catch (err) {
      console.error("Error toggling archiving:", err);
      setErrMsg("Error setting archiver: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function setScheme(event: React.ChangeEvent<HTMLInputElement>) {
    const scheme = parseInt(event.target.value, 10);
    if (scheme !== ProtocolScheme.mls10 && scheme !== ProtocolScheme.mlsSharedSecret) {
      console.error("Invalid scheme:", scheme);
      setErrMsg("Invalid scheme: " + scheme);
      return;
    }
    const membership = authContext.membership;
    if (!membership) { return; }
    if (!group) { return; }
    
    try {
      setSaving(true);
      const groupService = groupServiceFactory();
    
      await groupService.setProtocolScheme(membership.accountID, groupID, scheme);
      setGroup({...group, protocolScheme: scheme});
    } catch (err) {
      console.error("Error setting scheme:", err);
      setErrMsg("Error changing setting: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function resetGroupSecrets() {
    try {
      const membership = authContext.membership;
      if (!membership) { return; }
      const groupService = groupServiceFactory();
      await groupService.resetGroupSecrets(membership.accountID, groupID);
      setEnableReset(false);
      setFullyWelcomed(true);
    } catch (err) {
      console.error("Error resetting group secrets:", err);
      setErrMsg("Error resetting group secrets: " + (err as Error).message);
    }
  }

  if (!group) {
    return <div className="group-settings-container">
      <header className="group-header-hav attached-left-nav">
        <IconButton className="left-nav-outer-toggle" size="small"
            disableRipple disableTouchRipple
            onClick={groupNavContext.toggleGroupList}>
          <MenuIcon />
        </IconButton>
        <h3>Group Settings</h3>
      </header>
    </div>;
  }

  function renderOwnerForm(): React.ReactElement<any, any> {
    if (!group) {return <></>;} // make TS happy
    const settingLocked = fullyWelcomed ? "" : "setting-locked";
    return <>
      {!fullyWelcomed && <div className="form-row">
        <Alert className="group-secrets-alert" severity="warning">
          {!deviceAdded ? <>This device is not a member of this group. Some settings
            cannot be changed until it has been added to the group.</> : 
          <><p>This device has not received group secrets from another member yet.
            You can only change some settings until they have been received.</p><p>
              If you've exhausted all other options and no device is available to share
              secrets, you can reset the group. This will delete all prior content from
              the group, though! {}
            </p><div className="center-button-row">
              <FormControlLabel label="Are you sure?"
                control={<Switch checked={enableReset} onChange={() => setEnableReset(!enableReset)} />} />
              <Button disabled={!enableReset} variant="contained" color="error" onClick={resetGroupSecrets}>
                Reset Group
              </Button>
            </div></>}
        </Alert>
      </div>}
      <div className="form-row">
        <TextField id="groupname" name="groupname" fullWidth required
          label="Group Name" size="small"
          variant="outlined" color="secondary"
          value={group.groupname || ""} onChange={updateGroupName}  />
      </div>
      <div className="form-row">
        <TextField className="group-desc" id="desc" name="desc" fullWidth multiline
          variant="outlined" label="Group Description" color="secondary" size="small"
          value={group?.desc || ""} onChange={updateGroupDesc}  />
      </div>
      <div className="form-row">
      <FormControl component="fieldset" className={`group-type-selection ${settingLocked}`}>
        <FormLabel component="legend">Visibility &amp; Access</FormLabel>
        <RadioGroup name="grouptype" color="secondary"
            value={group.grouptype}
            onChange={e => updateValue({grouptype: e.target.value as GroupType})}>
          <FormLabel>
            <Radio value={GroupType.public} disabled={!fullyWelcomed} name="grouptype" disableRipple />
            <div className="radio-label-desc">
              <dt>Open</dt>
              <dd>Visible to members of this account. Members can join on their own.
                Guests must be added.
              </dd>
            </div>
          </FormLabel>
          <FormLabel>
            <Radio value={GroupType.private} disabled={!fullyWelcomed} name="grouptype" disableRipple />
            <div className="radio-label-desc">
              <dt>Private</dt>
              <dd>Not shown to members of this account. Invite only.</dd>
            </div>
          </FormLabel>
        </RadioGroup>
      </FormControl>
      </div>
      <div className="form-row">
      <FormControl component="fieldset" className={`group-encryption-scheme-selection ${settingLocked}`}>
      <FormLabel component="legend">Preserve Chat History</FormLabel>
        <RadioGroup name="protocolScheme" color="primary"
            value={group.protocolScheme || ProtocolScheme.mls10}
            onChange={setScheme}>
          <FormLabel>
            <Radio value={ProtocolScheme.mls10} disabled={!fullyWelcomed} name="protocolScheme" disableRipple />
            <div className="radio-label-desc">
              <dt>Disable</dt>
              <dd>Messages are only stored locally on participating devices. Devices only
                view the messages sent while they're participating in the group. 
              </dd>
            </div>
          </FormLabel>
          <FormLabel>
            <Radio value={ProtocolScheme.mlsSharedSecret} disabled={!fullyWelcomed} name="protocolScheme" disableRipple
              color="error" />
            <div className="radio-label-desc">
              <dt>Enable</dt>
              <dd>
                <span>An encrypted copy of each message is stored with the server, so that newly
                added devices may download, decrypt, and view the chat history from prior to
                the point they joined the group.
                </span>
                <em className="warning"> While this maintains end-to-end encryption,
                  it severely weakens the security of the group!
                </em>
              </dd>
            </div>
          </FormLabel>
        </RadioGroup>
      </FormControl>
      </div>
      {renderArchiving()}

    </>;
  }

  function renderArchiving(): React.ReactElement<any, any> {
    if (!canToggleArchiving || !group) { return <></>; }
      return <div className="form-row">
      <FormControl component="fieldset" className="group-archiving-toggle" fullWidth>
        <FormLabel component="legend">Group Archiving</FormLabel>
        <FormControlLabel control={
          <Checkbox color={group.archiving ? "primary" : "secondary"}
            disableFocusRipple={true}
            disableRipple={true}
            disabled={!canToggleArchiving || !fullyWelcomed}
            checked={group.archiving}
            onChange={toggleArchiving}
          />}
          label="Archive Group Messages"
        />
      </FormControl>
    </div>
  }

  return <div className="group-settings-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={groupNavContext.toggleGroupList}>
        <MenuIcon />
      </IconButton>
      <h3>Group Settings: {group.groupname || groupNavContext.currentGroup?.groupname || ""}</h3>
    </header>
    <section>
    <form className="form-wrapper">
      { errMsg && <Alert
        variant="filled"
        severity="error">{errMsg}</Alert> }
      {groupNavContext.currentGroup?.grouptype === GroupType.dm ?
        renderArchiving() : <>

        {groupNavContext.can(PermissionLevel.WRITE, "info") && renderOwnerForm()}
        <div className="form-row">
          <FormControl component="fieldset" className="group-type-selection">
            <FormLabel component="legend">Personal Notification Settings</FormLabel>
            <RadioGroup name="notifyOn" color="secondary"
                value={notifyOn}
                onChange={e => updateNotifyPreference({notifyOn: e.target.value as MemberNotifyOptions})}>
              <FormLabel>
                <Radio value={MemberNotifyOptions.none} name="notifyOn" disableRipple />
                <div className="radio-label-desc">
                  <dt>Mute Notifications</dt>
                  <dd>Do not send notifications to me from this group.</dd>
                </div>
              </FormLabel>
              <FormLabel>
                <Radio value={MemberNotifyOptions.mentions} name="notifyOn" disableRipple />
                <div className="radio-label-desc">
                  <dt>Mentions only</dt>
                  <dd>Only notify me when I'm mentioned in this group.</dd>
                </div>
              </FormLabel>
              <FormLabel>
                <Radio value={MemberNotifyOptions.all} name="notifyOn" disableRipple />
                <div className="radio-label-desc">
                  <dt>All</dt>
                  <dd>Notify me of all new messages posted to this group.</dd>
                </div>
              </FormLabel>
            </RadioGroup>
          </FormControl>
        </div>
      </>}
    </form>
    </section>
  </div>;
}

/*<div className="form-row">
      <FormControl component="fieldset" className="group-type-selection">
        <FormLabel component="legend">Posting Behavior</FormLabel>
        <RadioGroup name="submissiontype" color="secondary"
            value={group.submissiontype}
            onChange={e => updateValue({submissiontype: e.target.value as GroupSubmissionType})}>
          <FormLabel>
            <Radio value={GroupSubmissionType.normal} name="submissiontype" disableRipple />
            <div className="radio-label-desc">
              <dt>Normal</dt>
              <dd>Group Owners, Managers, and Users can read and submit messages.</dd>
            </div>
          </FormLabel>
          <FormLabel>
            <Radio value={GroupSubmissionType.broadcastOnly} name="submissiontype" disableRipple />
            <div className="radio-label-desc">
              <dt>Broadcast Only</dt>
              <dd>Only group Owners and Managers can post. All group members can read.</dd>
            </div>
          </FormLabel>
        </RadioGroup>
      </FormControl>
      </div>
<FormLabel>
            <Radio value={GroupSubmissionType.dropbox} name="submissiontype" disableRipple />
            <div className="radio-label-desc">
              <dt>Drop Box</dt>
              <dd>Group Owners, Managers, and Users can post messages.
                Only Owners and Managers can read.</dd>
            </div>
          </FormLabel>*/
